import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Paper,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Close as CloseIcon,
  Menu as MenuIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Login as LoginIcon,
  RocketLaunch as RocketIcon,
  ArrowForward as ArrowForwardIcon,
  Support as SupportIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useTenantConfig } from '../hooks/useTenant';

const BRAND = {
  primary: '#1b1132',
  accent: '#4f46e5',
  accentLight: '#818cf8',
  success: '#16a34a',
  successDark: '#15803d',
  white: '#ffffff',
  gray50: '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray400: '#94a3b8',
  gray600: '#475569',
  gray800: '#1e293b',
};

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const TopBarWrap = styled(Box)(({ scrolled }) => ({
  background: BRAND.primary,
  color: 'white',
  height: scrolled === 'true' ? '0px' : '40px',
  overflow: 'hidden',
  transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'flex',
  alignItems: 'center',
}));

const StyledAppBar = styled(AppBar)(({ scrolled }) => ({
  background: scrolled === 'true' 
    ? 'rgba(255, 255, 255, 0.85)' 
    : 'rgba(255, 255, 255, 0.75)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  boxShadow: scrolled === 'true'
    ? '0 1px 0 0 rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.08)'
    : '0 1px 0 0 rgba(0,0,0,0.03)',
  borderBottom: `1px solid ${scrolled === 'true' ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.04)'}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const NavBtn = styled(Button)(({ active }) => ({
  position: 'relative',
  fontWeight: 600,
  fontSize: '0.9rem',
  letterSpacing: '-0.01em',
  color: active === 'true' ? BRAND.accent : BRAND.gray800,
  textTransform: 'none',
  padding: '9px 18px',
  borderRadius: '12px',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  background: active === 'true' ? 'rgba(79, 70, 229, 0.06)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(79, 70, 229, 0.08)',
    color: BRAND.accent,
  },
}));

const LoginBtn = styled(Button)({
  fontWeight: 700,
  fontSize: '0.88rem',
  letterSpacing: '-0.01em',
  color: 'white',
  background: `linear-gradient(135deg, ${BRAND.accent} 0%, ${BRAND.accentLight} 100%)`,
  textTransform: 'none',
  padding: '10px 24px',
  borderRadius: '12px',
  whiteSpace: 'nowrap',
  boxShadow: '0 2px 8px rgba(79, 70, 229, 0.25)',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: `linear-gradient(135deg, ${BRAND.accentLight} 0%, ${BRAND.accent} 100%)`,
    boxShadow: '0 4px 16px rgba(79, 70, 229, 0.35)',
    transform: 'translateY(-1px)',
  },
});
const MegaPaper = styled(Paper)({
  borderRadius: '20px',
  boxShadow: '0 24px 80px rgba(79,70,229,0.12), 0 8px 24px rgba(0,0,0,0.08), 0 0 0 1px rgba(79,70,229,0.08)',
  border: 'none',
  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
  overflow: 'hidden',
  opacity: 1,
  pointerEvents: 'auto',
  zIndex: 1400,
  animation: `${slideDown} 0.25s cubic-bezier(0.4, 0, 0.2, 1)`,
  width: '100%',
  maxHeight: 'calc(100vh - 100px)',
  overflowY: 'auto',
  minWidth: '700px',
});

const MenuItemBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  padding: '18px 24px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  borderRadius: '12px',
  '&:hover': {
    background: BRAND.gray50,
  },
});

const MenuIconBox = styled(Box)(({ iconcolor }) => ({
  width: 48,
  height: 48,
  borderRadius: '12px',
  background: `${iconcolor}10`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': { fontSize: 26, color: iconcolor },
}));

  const MegaMenuWrapper = styled(Box)(({ theme }) => ({
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1400,
    width: 'min(1100px, calc(100vw - 32px))',
    [theme.breakpoints.down('lg')]: {
      width: 'min(600px, calc(100vw - 40px))',
    },
    
    // Invisible bridge to prevent hover gap
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-20px',
      left: '-50px',
      right: '-50px',
      height: '24px',
    },
  }));

  const FeatureCard = styled(Box)({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px 18px',
    cursor: 'pointer',
    borderRadius: '16px',
    border: '1px solid rgba(0,0,0,0.04)',
    background: 'white',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent 0%, transparent 100%)',
      transition: 'all 0.3s ease',
    },
    '&:hover': {
      background: 'white',
      borderColor: 'rgba(79,70,229,0.15)',
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 32px rgba(79,70,229,0.12), 0 4px 12px rgba(0,0,0,0.06)',
      '&::before': {
        background: `linear-gradient(90deg, ${BRAND.accent} 0%, ${BRAND.accentLight} 100%)`,
      },
      '& .arrow-icon': {
        opacity: 1,
        transform: 'translateX(0)',
      },
      '& .icon-box': {
        transform: 'scale(1.1) rotate(-5deg)',
      },
    },
  });
  
  const IconBox = styled(Box)(({ iconcolor }) => ({
    width: 48,
    height: 48,
    borderRadius: '14px',
    background: `linear-gradient(135deg, ${iconcolor}15 0%, ${iconcolor}08 100%)`,
    border: `1px solid ${iconcolor}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '14px',
      background: `linear-gradient(135deg, ${iconcolor}25 0%, ${iconcolor}05 100%)`,
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '& svg': { 
      fontSize: 24, 
      color: iconcolor,
      position: 'relative',
      zIndex: 1,
    },
  }));

const FEATURES = [
    {
    key: 'web',
    title: 'Web Sitesi Yönetimi',
    desc: 'Web sitenizi özgürce tasarlayın',
    color: '#ac001d',
    icon: '/assets/icon/internet.webp',
    href: '/ozellikler/web',
  },
    {
    key: 'uyeler',
    title: 'Üye Yönetimi',
    desc: 'Derbis Uyumlu Üye Yönetimi - Gönüllü & Bağışçı Yönetimi',
    color: '#0891b2',
    icon: '/assets/icon/user-management.webp',
    href: '/ozellikler/uye-yonetimi',
  },
  {
    key: 'finans',
    title: 'Aidat ve Muhasebe Yönetimi',
    desc: 'Tüm Aidat - Bağış ve Muhasebe Süreçleri',
    color: '#f97316',
    icon: '/assets/icon/accounting.webp',
    href: '/ozellikler/finans-muhasebe',
  },
  {
    key: 'toplu_sms',
    title: 'Toplu SMS Sistemi',
    desc: 'Mobil Onaylı Toplu SMS Gönderimi ve Otomatik SMS Bilgilendirme Servisi',
    color: '#f87502',
    icon: '/assets/icon/instant-message.webp',
    href: '/ozellikler/toplu-sms',
  },
  {
    key: 'ebys',
    title: 'EBYS - Elektronik Belge Yönetimi',
    desc: 'E-İmza Tabanlı Alt Yapı ile EYP standardında Resmi Yazışmalar',
    color: '#09e2ac',
    icon: '/assets/icon/security.webp',
    href: '/ozellikler/ebys',
  },
    {
    key: 'toplu_bildirim',
    title: 'Ücretsiz Toplu Bildirim',
    desc: 'Mobil Uygulama ile Ücretsiz Anlık Bildirimler',
    color: '#031c4a',
    icon: '/assets/icon/notifications.webp',
    href: '/ozellikler/toplu-bildirim',
  },
    {
    key: 'video',
    title: 'Video Konferans Sistemi',
    desc: 'Online Toplantı ve Webinar Sistemi',
    color: '#0017c3',
    icon: '/assets/icon/video-conference.webp',
    href: '/ozellikler/video-konferans',
  },
  {
    key: 'projeler',
    title: 'Organizasyon - Proje Yönetimi',
    desc: 'Organizasyon, etkinlik ve Proje Yönetim Süreçleri',
    color: '#1519e5',
    icon: '/assets/icon/network.webp',
    href: '/ozellikler/projeler',
  },


];

const CORPORATE = [
  { key: 'nasil', label: 'Nasıl Çalışır?', desc: 'Adım adım kurulum süreci', icon: <RocketIcon />, path: '/nasil-calisir', color: '#6366f1' },
  { key: 'about', label: 'Hakkımızda', desc: 'Şirket ve ekibimiz', icon: <InfoIcon />, path: '/about', color: '#3b82f6' },
  { key: 'teknoloji', label: 'Teknoloji', desc: 'Kullandığımız altyapı', icon: <SecurityIcon />, path: '/teknoloji', color: '#8b5cf6' },
  { key: 'destek', label: 'Teknik Destek', desc: '7/24 destek merkezi', icon: <SupportIcon />, path: '/teknik-destek', color: '#16a34a' },
];

const NAV_ITEMS = [
  { key: 'home', label: 'Anasayfa', path: '/' },
  { key: 'solutions', label: 'Özellikler', path: '/solutions', mega: true },
  { key: 'pricing', label: 'Fiyatlandırma', path: '/ucretlendirme' },
  // { key: 'corporate', label: 'Kurumsal', path: '/about', dropdown: true },
  { key: 'contact', label: 'İletişim', path: '/iletisim' },
  {key:'blog', label: 'Blog', path: '/blog' },
];

const Navbar = () => {
  const { logo, tenantName } = useTenantConfig();
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileExp, setMobileExp] = useState(null);
  const [leaveTimer, setLeaveTimer] = useState(null);

    const menuTimeoutRef = useRef(null);
    const navRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setDrawerOpen(false);
  }, [location.pathname]);

  const go = (path) => { navigate(path); setOpenMenu(null); setDrawerOpen(false); };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const onEnter = (key) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
      menuTimeoutRef.current = null;
    }
    if (leaveTimer) { clearTimeout(leaveTimer); setLeaveTimer(null); }
    setOpenMenu(key);
  };
  const onLeave = () => {
    menuTimeoutRef.current = setTimeout(() => setOpenMenu(null), 200);
  };

    const handleMenuLeave = useCallback(() => {
      menuTimeoutRef.current = setTimeout(() => {
        setOpenMenu(null);
      }, 200);
    }, []);
  
    const handleMenuContentEnter = useCallback(() => {
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
        menuTimeoutRef.current = null;
      }
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        setLeaveTimer(null);
      }
    }, [leaveTimer]);

 const SolutionsMega = () => {
    const appBarBottom = navRef.current
      ? Math.round(navRef.current.getBoundingClientRect().bottom)
      : scrolled ? 62 : 102;
    return (
    <MegaMenuWrapper
      sx={{ top: `${appBarBottom - 36}px` }}
      onMouseEnter={handleMenuContentEnter}
      onMouseLeave={handleMenuLeave}
    >
      <MegaPaper>
        <Box sx={{ p: { xs: 2, md: 3, lg: 4 } }}>
          {/* Header */}
          <Box sx={{ mb: { xs: 1.5, lg: 3 }, pb: { xs: 1.5, lg: 2.5 }, borderBottom: `2px solid ${BRAND.gray100}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                width: 4, 
                height: 20, 
                borderRadius: 2, 
                background: `linear-gradient(180deg, ${BRAND.accent} 0%, ${BRAND.accentLight} 100%)` 
              }} />
              <Typography sx={{ 
                fontSize: { xs: '0.75rem', lg: '0.85rem' }, 
                fontWeight: 800, 
                color: BRAND.gray800,
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Tüm Özellikler
              </Typography>
            </Box>
            <Typography sx={{ 
              fontSize: { xs: '0.7rem', lg: '0.75rem' }, 
              color: BRAND.gray600,
              mt: 0.5,
              ml: 2.5
            }}>
              Dernek yönetiminiz için her şey bir arada
            </Typography>
          </Box>

          {/* Features Grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' }, 
            gap: { xs: '8px', lg: '12px' },
          }}>
            {FEATURES.map((item) => (
              <FeatureCard key={item.key} onClick={() => go(item.href)} sx={{ p: { xs: '12px 14px', lg: '20px 18px' }, gap: { xs: '10px', lg: '16px' } }}>
                <IconBox className="icon-box" iconcolor={item.color} sx={{ width: { xs: 38, lg: 48 }, height: { xs: 38, lg: 48 } }}>
                  <Box
                    component="img"
                    src={item.icon}
                    alt={item.title}
                    sx={{ width: { xs: 22, lg: 28 }, height: { xs: 22, lg: 28 }, objectFit: 'contain' }}
                  />
                </IconBox>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                    <Typography sx={{ 
                      fontWeight: 700, 
                      fontSize: { xs: '0.82rem', lg: '0.95rem' }, 
                      color: BRAND.gray800,
                      letterSpacing: '-0.01em',
                    }}>
                      {item.title}
                    </Typography>
                    <ArrowForwardIcon 
                      className="arrow-icon"
                      sx={{ 
                        fontSize: 14, 
                        color: BRAND.accent,
                        opacity: 0,
                        transform: 'translateX(-6px)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }} 
                    />
                  </Box>
                  <Typography sx={{ 
                    fontSize: { xs: '0.72rem', lg: '0.82rem' }, 
                    color: BRAND.gray600,
                    lineHeight: 1.4,
                  }}>
                    {item.desc}
                  </Typography>
                </Box>
              </FeatureCard>
            ))}
          </Box>
        </Box>
      </MegaPaper>
    </MegaMenuWrapper>
    );
  };


  const CorporateMega = () => {
    return (
      <MegaPaper
        onMouseEnter={() => { 
          if (leaveTimer) { 
            clearTimeout(leaveTimer); 
            setLeaveTimer(null); 
          } 
        }}
        onMouseLeave={onLeave}
      >
        <Box sx={{ p: '36px 40px' }}>
          {/* Grid Layout - 2 Columns */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '12px',
            mb: 3
          }}>
            {CORPORATE.map((item) => (
              <MenuItemBox key={item.key} onClick={() => go(item.path)}>
                <MenuIconBox iconcolor={item.color}>
                  {item.icon}
                </MenuIconBox>
                <Box>
                  <Typography sx={{ 
                    fontWeight: 600, 
                    fontSize: '0.95rem', 
                    color: BRAND.gray800,
                    mb: 0.4
                  }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ 
                    fontSize: '0.85rem', 
                    color: BRAND.gray600,
                    lineHeight: 1.5
                  }}>
                    {item.desc}
                  </Typography>
                </Box>
              </MenuItemBox>
            ))}
          </Box>
        </Box>
      </MegaPaper>
    );
  };

  return (
    <>
      {/* Top Bar */}
      <TopBarWrap scrolled={scrolled ? 'true' : 'false'}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 38 }}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, cursor: 'pointer' }}>
                <PhoneIcon sx={{ fontSize: 13, color: BRAND.accentLight }} />
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)' }}>0 850 244 45 50</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, cursor: 'pointer' }}
                onClick={() => window.open('https://wa.me/+908502444550', '_blank')}>
                <WhatsAppIcon sx={{ fontSize: 13, color: '#4ade80' }} />
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)' }}>WhatsApp Destek</Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </TopBarWrap>

      {/* Main AppBar */}
      <StyledAppBar position="sticky" scrolled={scrolled ? 'true' : 'false'} ref={navRef}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ py: { xs: 0.8, md: 0.9 }, minHeight: { xs: 56, md: 62 } }}>

            {/* Logo */}
            <Box onClick={() => go('/')} sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer', 
              mr: { md: 4 }, 
              flexShrink: 0,
              transform: 'scale(1.1)',
              transformOrigin: 'left center',
            }}>
              <img 
                src={logo.main}
                alt="Dernek Sistem Logo" 
                style={{ height: '65px', width: 'auto' }}
              />
            </Box>

            {/* Desktop Nav */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center', gap: 0.3 }}>
              {NAV_ITEMS.map((item) => {
                const active = item.mega
                  ? location.pathname.startsWith('/solutions')
                  : item.dropdown
                  ? ['/about', '/nasil-calisir', '/teknoloji', '/teknik-destek'].some(p => location.pathname.startsWith(p))
                  : isActive(item.path);
                const hasMenu = item.mega || item.dropdown;
                return (
                  <Box key={item.key} sx={{ position: 'relative' }}
                    onMouseEnter={() => hasMenu ? onEnter(item.key) : undefined}
                    onMouseLeave={() => hasMenu ? onLeave() : undefined}
                  >
                    <NavBtn
                      active={active ? 'true' : 'false'}
                      onClick={() => !hasMenu && go(item.path)}
                      endIcon={hasMenu ? (
                        <ArrowDownIcon sx={{ fontSize: '1rem !important', transition: 'transform 0.2s', transform: openMenu === item.key ? 'rotate(180deg)' : 'none' }} />
                      ) : null}
                    >
                      {item.label}
                    </NavBtn>
                    {openMenu === item.key && (
                      <>
                        {item.mega && openMenu === 'solutions' && <SolutionsMega />}
                        {item.dropdown && openMenu === 'corporate' && <CorporateMega />}
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Desktop CTA */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.2, flexShrink: 0 }}>
              <LoginBtn startIcon={<LoginIcon sx={{ fontSize: '15px !important' }} />}
                //onClick={() => window.open('https://app.superdernek.com', '_blank')}
                >
                Giriş Yap
              </LoginBtn>
              {/* <DemoBtn startIcon={<RocketIcon sx={{ fontSize: '15px !important' }} />}
                onClick={() => go('/basvuru')}>
                Ücretsiz Dene
              </DemoBtn> */}
            </Box>

            {/* Mobile Hamburger */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 'auto' }}>
              <IconButton onClick={() => setDrawerOpen(true)} sx={{
                color: BRAND.primary, border: `1.5px solid ${BRAND.gray200}`,
                borderRadius: '9px', p: 0.8,
                '&:hover': { background: `rgba(79,70,229,0.07)`, borderColor: BRAND.accent },
              }}>
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </StyledAppBar>

      {/* Mobile Drawer */}
      <Drawer 
        anchor="right" 
        open={drawerOpen} 
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ 
          sx: { 
            width: '100%',
            maxWidth: 420,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            boxShadow: '-4px 0 32px rgba(0,0,0,0.12)',
            borderLeft: `3px solid ${BRAND.accent}`,
          } 
        }}
      >

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          px: 3, 
          py: 2.2,
          borderBottom: `1px solid ${BRAND.gray100}`,
        }}>
          <img 
            src="/assets/super_dernek_logo2.webp" 
            alt="Logo" 
            style={{ height: '50px', width: 'auto' }}
          />
          <IconButton 
            onClick={() => setDrawerOpen(false)} 
            sx={{ 
              color: BRAND.gray800,
              background: BRAND.gray100,
              '&:hover': { 
                background: BRAND.gray200,
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <List sx={{ px: 2.5, pt: 2, pb: 3 }}>
          {/* Anasayfa */}
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton onClick={() => go('/')} sx={{ 
              borderRadius: '14px',
              py: 1.4,
              px: 2,
              background: location.pathname === '/' ? `linear-gradient(135deg, ${BRAND.accent}15 0%, ${BRAND.accentLight}10 100%)` : 'transparent',
              borderLeft: location.pathname === '/' ? `3px solid ${BRAND.accent}` : '3px solid transparent',
              transition: 'all 0.2s ease',
              '&:hover': { 
                background: `rgba(79,70,229,0.06)`,
              } 
            }}>
              <ListItemText 
                primary="Anasayfa" 
                primaryTypographyProps={{ 
                  fontWeight: 600, 
                  fontSize: '0.95rem', 
                  letterSpacing: '-0.01em',
                  color: location.pathname === '/' ? BRAND.accent : BRAND.gray800 
                }} 
              />
            </ListItemButton>
          </ListItem>

          {/* Özellikler */}
          <ListItem disablePadding sx={{ flexDirection: 'column', alignItems: 'stretch', mb: 0.5 }}>
            <ListItemButton onClick={() => setMobileExp(mobileExp === 'sol' ? null : 'sol')}
              sx={{ 
                borderRadius: '14px',
                py: 1.4,
                px: 2,
                background: mobileExp === 'sol' ? `rgba(79,70,229,0.06)` : 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  background: `rgba(79,70,229,0.06)`,
                } 
              }}>
              <ListItemText primary="Özellikler" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem', letterSpacing: '-0.01em', color: BRAND.gray800 }} />
              {mobileExp === 'sol' ? <ArrowUpIcon sx={{ color: BRAND.accent, fontSize: 20 }} /> : <ArrowDownIcon sx={{ color: BRAND.gray400, fontSize: 20 }} />}
            </ListItemButton>
            <Collapse in={mobileExp === 'sol'} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 1, pb: 0.5 }}>
                {FEATURES.map((item) => (
                  <ListItemButton key={item.key} onClick={() => go(item.href)}
                    sx={{ borderRadius: '8px', py: 0.9, '&:hover': { background: `rgba(79,70,229,0.06)` } }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Box sx={{ width: 25, height: 25, borderRadius: '6px', background: `${item.color}20`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box
                          component="img"
                          src={item.icon}
                          alt={item.title}
                          sx={{ width: 14, height: 14, objectFit: 'contain' }}
                        />
                      </Box>
                    </ListItemIcon>
                    <ListItemText primary={item.title} primaryTypographyProps={{ fontSize: '0.83rem', fontWeight: 500, color: BRAND.gray800 }} />
                  </ListItemButton>
                ))}
              </Box>
            </Collapse>
          </ListItem>

          {/* Fiyatlandırma */}
          <ListItem disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton onClick={() => go('/ucretlendirme')}
              sx={{ 
                borderRadius: '14px',
                py: 1.4,
                px: 2,
                background: location.pathname === '/ucretlendirme' ? `linear-gradient(135deg, ${BRAND.accent}15 0%, ${BRAND.accentLight}10 100%)` : 'transparent',
                borderLeft: location.pathname === '/ucretlendirme' ? `3px solid ${BRAND.accent}` : '3px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  background: `rgba(79,70,229,0.06)`,
                } 
              }}>
              <ListItemText 
                primary="Fiyatlandırma" 
                primaryTypographyProps={{ 
                  fontWeight: 600, 
                  fontSize: '0.95rem', 
                  letterSpacing: '-0.01em',
                  color: location.pathname === '/ucretlendirme' ? BRAND.accent : BRAND.gray800 
                }} 
              />
            </ListItemButton>
          </ListItem>

          {/* Kurumsal */}
          {/* <ListItem disablePadding sx={{ flexDirection: 'column', alignItems: 'stretch', mb: 0.4 }}>
            <ListItemButton onClick={() => setMobileExp(mobileExp === 'corp' ? null : 'corp')}
              sx={{ borderRadius: '10px', '&:hover': { background: `rgba(79,70,229,0.08)` } }}>
              <ListItemText primary="Kurumsal" primaryTypographyProps={{ fontWeight: 600, fontSize: '0.92rem', color: BRAND.gray800 }} />
              {mobileExp === 'corp' ? <ArrowUpIcon sx={{ color: BRAND.accent, fontSize: 20 }} /> : <ArrowDownIcon sx={{ color: BRAND.gray400, fontSize: 20 }} />}
            </ListItemButton>
            <Collapse in={mobileExp === 'corp'} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 1, pb: 0.5 }}>
                {CORPORATE.map((item) => (
                  <ListItemButton key={item.key} onClick={() => go(item.path)}
                    sx={{ borderRadius: '8px', py: 0.9, '&:hover': { background: `rgba(79,70,229,0.06)` } }}>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <Box sx={{ width: 25, height: 25, borderRadius: '6px', background: `${item.color}20`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 14, color: item.color } }}>
                        {item.icon}
                      </Box>
                    </ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.83rem', fontWeight: 500, color: BRAND.gray800 }} />
                  </ListItemButton>
                ))}
              </Box>
            </Collapse>
          </ListItem> */}

          {/* İletişim */}
          <ListItem disablePadding sx={{ mb: 2 }}>
            <ListItemButton onClick={() => go('/iletisim')}
              sx={{ 
                borderRadius: '14px',
                py: 1.4,
                px: 2,
                background: location.pathname === '/iletisim' ? `linear-gradient(135deg, ${BRAND.accent}15 0%, ${BRAND.accentLight}10 100%)` : 'transparent',
                borderLeft: location.pathname === '/iletisim' ? `3px solid ${BRAND.accent}` : '3px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  background: `rgba(79,70,229,0.06)`,
                } 
              }}>
              <ListItemText 
                primary="İletişim" 
                primaryTypographyProps={{ 
                  fontWeight: 600, 
                  fontSize: '0.95rem', 
                  letterSpacing: '-0.01em',
                  color: location.pathname === '/iletisim' ? BRAND.accent : BRAND.gray800 
                }} 
              />
            </ListItemButton>
          </ListItem>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ px: 2, pt: 1, pb: 2 }}>
            <Button 
              fullWidth 
              startIcon={<LoginIcon />}
              onClick={() => window.open('https://app.superdernek.com', '_blank')}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 700, 
                fontSize: '0.95rem',
                letterSpacing: '-0.01em',
                borderRadius: '14px', 
                color: 'white',
                background: `linear-gradient(135deg, ${BRAND.accent} 0%, ${BRAND.accentLight} 100%)`,
                py: 1.5,
                boxShadow: '0 4px 16px rgba(79,70,229,0.25)',
                transition: 'all 0.2s ease',
                '&:hover': { 
                  background: `linear-gradient(135deg, ${BRAND.accentLight} 0%, ${BRAND.accent} 100%)`,
                  boxShadow: '0 6px 20px rgba(79,70,229,0.35)',
                  transform: 'translateY(-2px)',
                } 
              }}>
              Giriş Yap
            </Button>
          </Box>
        </List>
      </Drawer>

      {/* WhatsApp Sticky */}
      <Box sx={{ position: 'fixed', bottom: 26, right: 26, zIndex: 9999 }}>
        <Box onClick={() => window.open(`https://wa.me/90XXXXXXXXXX?text=Merhaba, ${tenantName} hakkında bilgi almak istiyorum.`, '_blank')}
          sx={{
            width: 52, height: 52, borderRadius: '50%', background: '#25d366',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(37,211,102,0.5)',
            transition: 'all 0.25s ease',
            '&:hover': { transform: 'scale(1.1)', boxShadow: '0 6px 28px rgba(37,211,102,0.65)' },
          }}>
          <WhatsAppIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
      </Box>
    </>
  );
};

export default Navbar;
